// Shared print utility: convert a DOM node to PDF via html2pdf and open native print popup via hidden iframe
// Usage:
//   import { printDomAsPdfWithPopup } from '../../Shared/print/printUtils';
//   await printDomAsPdfWithPopup(node, { widthMM: 210, heightMM: 297, orientation: 'portrait' }); // A4 portrait
//   await printDomAsPdfWithPopup(node, { widthMM: 297, heightMM: 210, orientation: 'landscape' });  // A4 landscape

import html2pdf from 'html2pdf.js';
import { jsPDF } from 'jspdf';

// Global print progress event emitter
const printProgressListeners = new Set();
export function onPrintProgress(listener) {
  printProgressListeners.add(listener);
  return () => printProgressListeners.delete(listener);
}
function emitPrintProgress(progress, message) {
  printProgressListeners.forEach(fn => { try { fn(progress, message); } catch(_) {} });
}

export function getPPI() {
  if (typeof window === 'undefined') return 96;
  const div = document.createElement('div');
  div.style.width = '1in';
  div.style.height = '1in';
  div.style.position = 'absolute';
  div.style.left = '-10000px';
  document.body.appendChild(div);
  const ppi = div.offsetWidth || 96;
  document.body.removeChild(div);
  return ppi;
}

async function waitForImages(node) {
  const imgs = node ? node.getElementsByTagName('img') : [];
  await Promise.all(Array.from(imgs).map(img => new Promise(res => {
    if (img.complete) return res();
    img.onload = () => res();
    img.onerror = () => res();
  })));
}

export async function printDomAsPdfWithPopup(node, opts = {}) {
  if (!node) throw new Error('printDomAsPdfWithPopup: node is required');
  const {
    widthMM = 210, // default A4 portrait width
    heightMM = 297, // default A4 portrait height
    orientation = 'portrait', // 'portrait' | 'landscape'
    scale = 3,
    background = '#ffffff',
    heightBufferFactor = 1.0,
    onProgress,
  } = opts;

  // Progress callback helper - also emits globally
  const updateProgress = (progress, message) => {
    emitPrintProgress(progress, message);
    if (onProgress && typeof onProgress === 'function') {
      onProgress(progress, message);
    }
  };

  // Ensure content is ready
  await waitForImages(node);

  // Temporarily ensure node and its print wrapper are visible for measurement
  let wrapper = node.closest('.print-parent') || node.closest('[class*="print"]') || node.closest('.print-container-shift-summary');
  
  // If no wrapper found, use the parent element
  if (!wrapper && node.parentElement) {
    wrapper = node.parentElement;
  }
  const prevWrapper = wrapper ? {
    position: wrapper.style.position,
    top: wrapper.style.top,
    left: wrapper.style.left,
    width: wrapper.style.width,
    height: wrapper.style.height,
    maxHeight: wrapper.style.maxHeight,
    overflow: wrapper.style.overflow,
    opacity: wrapper.style.opacity,
    zIndex: wrapper.style.zIndex,
    visibility: wrapper.style.visibility,
    display: wrapper.style.display,
    pointerEvents: wrapper.style.pointerEvents,
  } : null;
  const prev = {
    position: node.style.position,
    top: node.style.top,
    left: node.style.left,
    width: node.style.width,
    maxHeight: node.style.maxHeight,
    overflow: node.style.overflow,
    opacity: node.style.opacity,
    zIndex: node.style.zIndex,
    visibility: node.style.visibility,
  };
  if (wrapper) {
    wrapper.style.position = 'static';
    wrapper.style.top = '0';
    wrapper.style.left = '0';
    wrapper.style.width = '100%';
    wrapper.style.height = `${node.scrollHeight}px`;
    wrapper.style.minHeight = `${node.scrollHeight}px`;
    wrapper.style.maxHeight = 'none';
    wrapper.style.overflow = 'visible';
    wrapper.style.opacity = '1';
    wrapper.style.visibility = 'visible';
    wrapper.style.zIndex = 'auto';
    wrapper.style.pointerEvents = 'auto';
    
    // Do not force A4 width; let size derive from target width later
    wrapper.style.minWidth = 'auto';
  }
  node.style.height = `${node.scrollHeight}px`;
  node.style.minHeight = `${node.scrollHeight}px`;
  node.style.maxHeight = 'none';
  node.style.overflow = 'visible';
  let targetWidthMM = widthMM;
  let targetHeightMM = heightMM;
  if (orientation === 'landscape' && widthMM < heightMM) {
    targetWidthMM = heightMM;
    targetHeightMM = widthMM;
  }
  if (orientation === 'portrait' && widthMM > heightMM) {
    targetWidthMM = heightMM;
    targetHeightMM = widthMM;
  }

  node.style.position = 'static';
  node.style.top = '0';
  node.style.left = '0';
  // Force the node width to the printable width (paper width - margins) for correct pagination
  const layoutMarginMM = 10; // base assumption before we choose final margins
  const printableWidthMM = Math.max(10, targetWidthMM - (layoutMarginMM * 2));
  node.style.width = `${printableWidthMM}mm`;
  node.style.maxHeight = 'none';
  node.style.overflow = 'visible';
  node.style.opacity = '1';
  node.style.visibility = 'visible';
  node.style.zIndex = 'auto';

  // Allow a longer layout cycle before measuring to ensure content is fully rendered
  await new Promise(r => setTimeout(r, 1000));
  
  // Force a reflow to ensure layout is computed
  void node.offsetHeight;

  // Compute height in mm (not used to set PDF height anymore; we paginate on A4)
  const ppi = getPPI();
  const pxToMM = (px) => px * (25.4 / ppi);
  const measured = pxToMM(node.scrollHeight) * heightBufferFactor;
  const contentHeightMM = Math.max(40, measured);

  // Emit start progress
  updateProgress(5, 'Preparing document...');

  // For large datasets, use chunked approach
  const isLargeContent = node.scrollHeight > 10000; // More than ~10kpx is large
  
  if (isLargeContent) {
    console.log('Large content detected, using chunked PDF generation');
    updateProgress(8, 'Preparing large document...');
    return await generateChunkedPDF(node, { scale, background, orientation, onProgress: updateProgress });
  }
  
  updateProgress(20, 'Generating PDF...');
  // Choose margins: narrow margins for thermal widths
  const isThermal = targetWidthMM <= 80;
  const marginMM = isThermal ? 1 : 10; // 1mm for 80/58mm, 10mm for reports
  const opt = {
    margin: marginMM,
    html2canvas: {
      scale: Math.min(scale, 2),
      useCORS: true,
      backgroundColor: background,
      scrollX: 0,
      scrollY: 0
    },
    // Honor requested paper size
    jsPDF: { unit: 'mm', format: [targetWidthMM, targetHeightMM], orientation },
    pagebreak: { mode: ['css', 'legacy'], avoid: ['tr', 'td', '.avoid-break', '.grand-total'] },
  };

  try {
    updateProgress(40, 'Rendering content...');
    const pdfBlob = await html2pdf().from(node).set(opt).outputPdf('blob');
    updateProgress(90, 'Finalizing PDF...');
    const url = URL.createObjectURL(pdfBlob);
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);
    iframe.src = url;
    updateProgress(100, 'Opening print dialog...');
    iframe.onload = () => {
      const cw = iframe.contentWindow;
      if (!cw) return;
      const cleanup = () => {
        try { URL.revokeObjectURL(url); } catch (_) {}
        try { document.body.removeChild(iframe); } catch (_) {}
      };

      cw.onafterprint = () => {
        cleanup();
        cw.onafterprint = null;
      };

      try {
        const mql = cw.matchMedia ? cw.matchMedia('print') : null;
        if (mql && mql.addListener) {
          mql.addListener((e) => {
            if (!e.matches) {
              cleanup();
              try { mql.removeListener(); } catch (_) {}
            }
          });
        }
      } catch (_) {}

      const failSafe = setTimeout(() => { cleanup(); }, 30000);
      const originalAfterPrint = cw.onafterprint;
      cw.onafterprint = () => {
        clearTimeout(failSafe);
        if (originalAfterPrint) try { originalAfterPrint(); } catch (_) {}
        cleanup();
        cw.onafterprint = null;
      };

      cw.focus();
      cw.print();
    };
  } finally {
    // Restore node styles
    node.style.position = prev.position;
    node.style.top = prev.top;
    node.style.left = prev.left;
    node.style.width = prev.width;
    node.style.maxHeight = prev.maxHeight;
    node.style.overflow = prev.overflow;
    node.style.opacity = prev.opacity;
    node.style.visibility = prev.visibility;
    node.style.zIndex = prev.zIndex;
    if (wrapper && prevWrapper) {
      wrapper.style.position = prevWrapper.position;
      wrapper.style.top = prevWrapper.top;
      wrapper.style.left = prevWrapper.left;
      wrapper.style.width = prevWrapper.width;
      wrapper.style.height = prevWrapper.height;
      wrapper.style.maxHeight = prevWrapper.maxHeight;
      wrapper.style.overflow = prevWrapper.overflow;
      wrapper.style.opacity = prevWrapper.opacity;
      wrapper.style.visibility = prevWrapper.visibility;
      wrapper.style.display = prevWrapper.display;
      wrapper.style.zIndex = prevWrapper.zIndex;
      wrapper.style.pointerEvents = prevWrapper.pointerEvents;
    }
  }
}

// Working chunked PDF generation for large datasets
async function generateChunkedPDF(node, { scale = 1.5, background = '#ffffff', orientation = 'portrait', onProgress }) {
  const { jsPDF } = await import('jspdf');
  const { default: html2canvas } = await import('html2canvas');
  
  // A4 dimensions in mm
  const pageHeightMM = orientation === 'portrait' ? 297 : 210;
  const pageWidthMM = orientation === 'portrait' ? 210 : 297;
  const marginMM = 5;
  const printableHeightMM = pageHeightMM - (marginMM * 2);
  const printableWidthMM = pageWidthMM - (marginMM * 2);
  
  // Calculate pixels per mm
  const ppi = getPPI();
  const pxPerMM = ppi / 25.4;
  const pageHeightPx = printableHeightMM * pxPerMM;
  
  // Create PDF
  const pdf = new jsPDF({ orientation, unit: 'mm', format: 'a4' });
  
  console.log(`Starting chunked PDF for ${node.scrollHeight}px content`);
  const startTime = performance.now();
  
  if (onProgress) onProgress(10, 'Initializing PDF generation...');
  
  let currentY = 0;
  let pageNumber = 1;
  const totalPages = Math.ceil(node.scrollHeight / pageHeightPx);
  
  while (currentY < node.scrollHeight) {
    const progress = Math.min(90, 10 + ((pageNumber / totalPages) * 80));
    if (onProgress) onProgress(progress, `Generating page ${pageNumber}/${totalPages}...`);
    console.log(`Generating page ${pageNumber}/${totalPages} (Y: ${currentY})`);
    
    // Create a temporary container for this chunk
    const chunkContainer = document.createElement('div');
    chunkContainer.style.position = 'absolute';
    chunkContainer.style.left = '-9999px';
    chunkContainer.style.top = '0';
    chunkContainer.style.width = `${printableWidthMM}mm`;
    chunkContainer.style.height = `${pageHeightPx}px`;
    chunkContainer.style.overflow = 'hidden';
    
    // Clone the node and position it for this chunk
    const nodeClone = node.cloneNode(true);
    nodeClone.style.marginTop = `-${currentY}px`;
    nodeClone.style.width = `${printableWidthMM}mm`;
    nodeClone.style.position = 'relative';
    
    chunkContainer.appendChild(nodeClone);
    document.body.appendChild(chunkContainer);
    
    try {
      // Wait for rendering
      await new Promise(r => setTimeout(r, 300));
      
      // Capture this chunk
      const canvas = await html2canvas(chunkContainer, {
        scale: Math.min(scale, 1.5),
        useCORS: true,
        backgroundColor: background,
        width: printableWidthMM * pxPerMM,
        height: pageHeightPx,
        logging: false,
        removeContainer: true,
        allowTaint: true
      });
      
      // Add page to PDF
      if (pageNumber > 1) {
        pdf.addPage();
      }
      
      // Add image to PDF
      const imgData = canvas.toDataURL('image/png', 0.8);
      pdf.addImage(imgData, 'PNG', marginMM, marginMM, printableWidthMM, printableHeightMM);
      
    } catch (error) {
      console.error(`Error generating page ${pageNumber}:`, error);
    } finally {
      // Clean up
      document.body.removeChild(chunkContainer);
    }
    
    currentY += pageHeightPx;
    pageNumber++;
  }
  
  const endTime = performance.now();
  console.log(`Chunked PDF completed in ${(endTime - startTime).toFixed(2)}ms`);
  
  if (onProgress) onProgress(95, 'Finalizing PDF...');
  
  // Convert to blob and open print dialog
  const pdfBlob = pdf.output('blob');
  console.log('PDF blob size:', (pdfBlob.size / 1024 / 1024).toFixed(2) + 'MB');
  
  if (onProgress) onProgress(100, 'Opening print dialog...');
  
  const url = URL.createObjectURL(pdfBlob);
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);
  iframe.src = url;
  iframe.onload = () => {
    const cw = iframe.contentWindow;
    if (!cw) return;
    const cleanup = () => {
      try { URL.revokeObjectURL(url); } catch (_) {}
      try { document.body.removeChild(iframe); } catch (_) {}
    };

    cw.onafterprint = () => {
      cleanup();
      cw.onafterprint = null;
    };

    try {
      const mql = cw.matchMedia ? cw.matchMedia('print') : null;
      if (mql && mql.addListener) {
        mql.addListener((e) => {
          if (!e.matches) {
            cleanup();
            try { mql.removeListener(); } catch (_) {}
          }
        });
      }
    } catch (_) {}

    const failSafe = setTimeout(() => { cleanup(); }, 30000);
    const originalAfterPrint = cw.onafterprint;
    cw.onafterprint = () => {
      clearTimeout(failSafe);
      if (originalAfterPrint) try { originalAfterPrint(); } catch (_) {}
      cleanup();
      cw.onafterprint = null;
    };

    cw.focus();
    cw.print();
  };
}
